 
import AchievedCorrectionMetrics from '../Alignment/AchievedCorrectionMetrics';
import AlignmentValidation from '../Alignment/AlignmentValidation';
import { CaseReport } from '../../models/CaseTypes';
import SpineViewer from '../Alignment/SpineViewer120';
import { useAchievedCorrection } from '../Alignment/useAchievedCorrection';

/**
 * Component to display the Achieved Correction data
 * Shows comparison between pre-op and other measurement points (intra-op, post-op)
 */
const AchievedCorrection = ({ caseReport } : {caseReport:CaseReport}):
  React.JSX.Element => {
  
  const {
    loading,
    error,
    metricSessions,
    primarySession,
    comparisonSession,
    primaryMeshSignedUrl,
    comparisonMeshSignedUrl,
    selectedVertebraChecks,
    selectedVertebraeNames,
    selectedPlaneNames,
    preOpData,
    comparisonData,
    validationSignedUrl,
    handlePrimaryChange,
    handleComparisonChange,
    handleVertebraCheckboxChange,
  } = useAchievedCorrection(caseReport);

  if (loading && (!preOpData.length || !comparisonData.length)) {
    return <div className="loading">Loading alignment data...</div>;
  }

  if (error || !metricSessions.length) {
    return (
      <div className="alignment-measurements">
        <div className="warning-message">
          Alignment data is not available for this case.
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 lg:gap-5">

      {/* LAYOUT_INSTANCE Mobile Layout (< 440px) - Vertical stacking of all elements */}
      <div className="min-[440px]:hidden rounded-[var(--radius-card)] border border-border overflow-hidden flex flex-col">
        {/* Header */}
        <div className="section-header" style={{ border: 'none' }}>
          <h3 className="mb-0">
            Alignment Measurements
          </h3>
        </div>

        {/* Pre Op Alignment Section */}
        <div className="flex flex-col">
          
          {/* Pre Op Dropdown */}
          <SpineViewer
            id="primary-select"
            metricSessions={metricSessions}
            onSessionChange={handlePrimaryChange}
            session={primarySession}
            selectedPlaneNames={selectedPlaneNames}
            selectedVertebraeNames={selectedVertebraeNames}
            signedUrl={primaryMeshSignedUrl}
          />
        </div>

        {/* Post Op Alignment Section */}
        <div className="flex flex-col">
          {/* Post Op Dropdown */}
          <SpineViewer
            id="comparison-select"
            metricSessions={metricSessions}
            onSessionChange={handleComparisonChange}
            session={comparisonSession}
            selectedPlaneNames={selectedPlaneNames}
            selectedVertebraeNames={selectedVertebraeNames}
            signedUrl={comparisonMeshSignedUrl}
          />
        </div>

        {/* 3D Alignment Measurements Table */}
        <div
          className="h-[380px] flex items-center justify-center border-t"
          style={{
            backgroundColor: '#E8EAED',
            borderColor: '#D4D4D5',
          }}
        >
          <AchievedCorrectionMetrics
            comparisonData={comparisonData}
            comparisonSession={comparisonSession}
            primarySession={primarySession}
            selectedVertebrae={selectedVertebraChecks}
            onVertebraeCheckboxChange={handleVertebraCheckboxChange}
          />
        </div>
      </div>

      {/* LAYOUT_INSTANCE Tablet Layout (440px - 739px) - Vertical stacking with alignment container on top */}
      <div className="hidden min-[440px]:flex min-[740px]:hidden flex-col gap-[19px]">
        {/* Alignment Measurements Container */}
        <div className="rounded-[var(--radius-card)] overflow-hidden flex flex-col w-full">
          {/* Header */}
          <div className="section-header">
            <h3 className="mb-0">
              Alignment Measurements
            </h3>
          </div>

          {/* Two Column Dropdowns and Views */}
          <div className="flex items-start w-full">
            {/* Pre Op Alignment Column */}
            <div className="flex-1 flex flex-col">

              {/* Pre Op Dropdown */}
              <SpineViewer
                id="primary-select"
                metricSessions={metricSessions}
                onSessionChange={handlePrimaryChange}
                session={primarySession}
                selectedPlaneNames={selectedPlaneNames}
                selectedVertebraeNames={selectedVertebraeNames}
                signedUrl={primaryMeshSignedUrl}
              />
            </div>

            {/* Post Op Alignment Column */}
            <div className="flex-1 flex flex-col">
              {/* Post Op Dropdown */}
              <SpineViewer
                id="comparison-select"
                metricSessions={metricSessions}
                onSessionChange={handleComparisonChange}
                session={comparisonSession}
                selectedPlaneNames={selectedPlaneNames}
                selectedVertebraeNames={selectedVertebraeNames}
                signedUrl={comparisonMeshSignedUrl}
              />
            </div>
          </div>
        </div>

        {/* 3D Alignment Measurements Table */}
        <div
          className="relative rounded-[var(--radius-card)] h-[470px] flex items-center justify-center w-full"
          style={{ backgroundColor: '#E8EAED' }}
        >
          <AchievedCorrectionMetrics
            comparisonData={comparisonData}
            comparisonSession={comparisonSession}
            primarySession={primarySession}
            selectedVertebrae={selectedVertebraChecks}
            onVertebraeCheckboxChange={handleVertebraCheckboxChange}
          />
        </div>
      </div>

      {/* LAYOUT_INSTANCE Desktop Layout (>= 740px) - Two column grid */}
      <div className="hidden min-[740px]:grid grid-cols-2 gap-[19px]">
        {/* Left Column: Alignment Measurements with Header and Dropdowns */}
        <div className="rounded-[var(--radius-card)] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="section-header">
            <h3 className="mb-0">
              Alignment Measurements
            </h3>
          </div>

          {/* Two Column Dropdowns and Views */}
          <div className="flex items-start w-full">
            {/* Pre Op Alignment Column */}
            <div className="flex-1 flex flex-col">

              {/* Pre Op Dropdown */}
              <SpineViewer
                id="primary-select"
                metricSessions={metricSessions}
                onSessionChange={handlePrimaryChange}
                session={primarySession}
                selectedPlaneNames={selectedPlaneNames}
                selectedVertebraeNames={selectedVertebraeNames}
                signedUrl={primaryMeshSignedUrl}
              />
            </div>

            {/* Post Op Alignment Column */}
            <div className="flex-1 flex flex-col">
              {/* Post Op Dropdown */}
              <SpineViewer
                id="comparison-select"
                metricSessions={metricSessions}
                onSessionChange={handleComparisonChange}
                session={comparisonSession}
                selectedPlaneNames={selectedPlaneNames}
                selectedVertebraeNames={selectedVertebraeNames}
                signedUrl={comparisonMeshSignedUrl}
              />
            </div>
          </div>
        </div>

        {/* Right Column: 3D Alignment Measurements Table */}
        <div
          data-testid="alignment-measurements-container"
          className="relative rounded-[12px] h-[470px] flex items-center justify-center border border-solid border-border"
        >
          <AchievedCorrectionMetrics
            comparisonData={comparisonData}
            comparisonSession={comparisonSession}
            primarySession={primarySession}
            selectedVertebrae={selectedVertebraChecks}
            onVertebraeCheckboxChange={handleVertebraCheckboxChange}
          />
        </div>
      </div>

      { validationSignedUrl && (
        <AlignmentValidation validationSignedUrl={validationSignedUrl} />
      )}
    </div>
  );
};

export default AchievedCorrection;
